import { HttpService } from '@Services/api/httpService.ts';
import { IPartnerDataResponse } from '@Types/types.ts';
import { useBoundStore } from '@Store/useBoundStore.ts';
import {
  ObservabilityErrorSeverity,
  reportError,
} from '@Services/observability/initObservability.ts';
import { updateCustomerDataFromServer } from '@Services/init/util.ts';
import { identifyTrackingServiceUser } from '@Services/analytics/tracking.service.ts';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types.ts';
import { isProdEnv } from '@Services/env/environment.service';

export const handlePartnerFlowInitialData = async (token: string): Promise<void> => {
  if (!token) {
    //TODO: Verify nothing else is needed
    return;
  }

  try {
    const { data } = await HttpService.get(`v3/onboarding?token=${token}`);
    const partnerData = data as IPartnerDataResponse;

    if (partnerData.partnerId) {
      setupPartnerOnboardingData(partnerData);
    }

    if (useBoundStore.getState().onboardingSlice.getIsEmbedded()) {
      await setupPasswordlessOnboardingData(token);
    }
  } catch (e) {
    const errorToReport = {
      metadata: {
        name: 'handlePartnerFlowInitialData',
        message: JSON.stringify(e),
      },
      severity: ObservabilityErrorSeverity.ERROR,
    };
    reportError(errorToReport);
  }
};

const setupPartnerOnboardingData = (partnerData: IPartnerDataResponse) => {
  const setIsPartnerFlow = useBoundStore.getState().onboardingSlice.setIsPartnerFlow;
  const setOnboardingIdentifier = useBoundStore.getState().onboardingSlice.setOnboardingIdentifier;
  const customerData = useBoundStore.getState().customer;
  const setPartnerName = useBoundStore.getState().onboardingSlice.setPartnerName;

  const isInsideIframe = isProdEnv()
    ? window.parent !== window && window.self !== window.top
    : true;

  // @TODO check useage
  setIsPartnerFlow(true);
  setPartnerName(partnerData.partnerName);
  setOnboardingIdentifier(
    isInsideIframe
      ? OnboardingFlowIdentifier.PARTNER_EMBEDDED
      : OnboardingFlowIdentifier.PARTNER_CONNECT
  );
  const customerDataToUpdate = { ...customerData };
  customerDataToUpdate.customerData.customerBusinessData.businessName =
    partnerData.partnerName || '';
  customerDataToUpdate.customerData.email = partnerData.email || '';
  customerDataToUpdate.customerData.firstName = partnerData.firstName || '';
  customerDataToUpdate.customerData.lastName = partnerData.lastName || '';
  customerDataToUpdate.customerLogoUrl = `https://assets.lili.co/connect/${partnerData.partnerId}/ob_logo.png`;
  customerDataToUpdate.partnerId = partnerData.partnerId;
  updateCustomerDataFromServer(customerDataToUpdate, customerData);
};

const setupPasswordlessOnboardingData = async (token: string) => {
  const getCustomerData = useBoundStore.getState().getCustomerData;
  const setKybData = useBoundStore.getState().setKybData;

  const { data: onboardingPartnerData } = await HttpService.get(
    `v3/onboardingPartner?token=${token}`
  );

  identifyTrackingServiceUser(onboardingPartnerData.customerId);
  let customerDataToUpdate = { ...getCustomerData() };
  customerDataToUpdate = {
    ...customerDataToUpdate,
    accessToken: onboardingPartnerData.accessToken,
    customerId: onboardingPartnerData.customerId,
    isNonUsSupported: onboardingPartnerData.nonUsSupported,
    onboardingStatus: onboardingPartnerData.onboardingStatus,
  };

  customerDataToUpdate.customerData = {
    ...customerDataToUpdate.customerData,
    birthDate: onboardingPartnerData.birthDate,
    customerAddressData: {
      ...customerDataToUpdate?.customerData?.customerAddressData,
      ...onboardingPartnerData.customerAddressData,
    },
    email: onboardingPartnerData.email,
    firstName: onboardingPartnerData.firstName,
    lastName: onboardingPartnerData.lastName,
    phoneNumber: onboardingPartnerData.phoneNumber,
    ssn: onboardingPartnerData.ssn,
    lastStepData: onboardingPartnerData.lastStepData,
    onboardingTypeEnum: onboardingPartnerData.onboardingTypeEnum,
  };

  setKybData({ business: onboardingPartnerData.business });
  const baseData = getCustomerData();
  updateCustomerDataFromServer(baseData, customerDataToUpdate);
};
